import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React from "react";

import BaseLink from "~/components/links/BaseLink";
import ErrorBoundary from "~/components/ErrorBoundary";
import DropDownNavButton from "~/components/buttons/DropDownNavButton";
import FaIcon from "~/components/FaIcon";

import { useTranslationContext } from "~/context/TranslationContext";

import transition from "~/utilities/transition";
import trackNavigationEvent from "~/utilities/trackNavigationEvent";
import { sanitizeForId } from "~/utilities/helpers";
import { languageLabel } from "~/utilities/translations";

const Label = styled.span`
    ${transition({}, "border-bottom")};
    border-bottom: 2px solid transparent;
    line-height: 1.42;
`;

const MainNavLinkWrapper = styled(BaseLink)`
    font-size: 2rem;
    color: ${({ theme }) => theme.colors.typography.defaultLightBackground};

    &:hover,
    &:focus {
        ${Label} {
            border-bottom: 2px solid ${({ theme }) => theme.palette.teal};
        }
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        padding: 28px 15px;
    }
`;

export const Icon = styled(FaIcon)`
    margin-right: 10px;
`;

const MainNavLink = ({
    buttonColor,
    dropdownLabel,
    hasDropdown = false,
    isNavOpened = null,
    linkLabel = "",
    linkUrl = "",
    dispatch = () => {},
    newTabOverride = "",
    icon = "",
    openedSubnavs = {},
    submenuId = "",
    onClick = () => {},
    screenReaderText = "",
    linkNativeName = null,
    linkTranslatedName = null,
    languageCode = null,
}) => {
    const linkProps = {
        newTabOverride,
        to: linkUrl,
        onClick: () => trackNavigationEvent(false, 1, linkLabel),
    };

    const { activeCode } = useTranslationContext();

    const translatedLabel = languageLabel(
        linkLabel,
        linkNativeName,
        linkTranslatedName,
        languageCode,
        activeCode,
    );

    const subNavButtonProps = {
        icon,
        screenReaderText,
        onClick: () => {
            if (onClick) {
                onClick();
            }
            trackNavigationEvent(true, 1, linkLabel);
            dispatch({
                type: "HANDLE_SUB_NAV",
                id: submenuId,
                currentSubNavs: openedSubnavs,
                level: "secondary",
            });
        },
        "aria-expanded": isNavOpened,
        "data-label": `${sanitizeForId(linkNativeName ?? linkLabel)}-Top`,
        isNavOpened,
        linkLabel: dropdownLabel ?? translatedLabel,
    };

    return (
        <ErrorBoundary>
            {!!hasDropdown ? (
                <DropDownNavButton
                    buttonColor={buttonColor}
                    {...subNavButtonProps}
                />
            ) : (
                <MainNavLinkWrapper {...linkProps}>
                    {icon ? <Icon name={icon} /> : null}
                    <Label>{translatedLabel}</Label>
                </MainNavLinkWrapper>
            )}
        </ErrorBoundary>
    );
};

MainNavLink.propTypes = {
    hasDropdown: PropTypes.bool,
    isNavOpened: PropTypes.bool,
    linkLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    linkUrl: PropTypes.string,
    newTabOverride: PropTypes.string,
    icon: PropTypes.string,
    dispatch: PropTypes.func,
    openedSubnavs: PropTypes.shape({}),
    submenuId: PropTypes.string,
    onClick: PropTypes.func,
    dropdownLabel: PropTypes.string,
    screenReaderText: PropTypes.string,
    linkNativeName: PropTypes.string,
    linkTranslatedName: PropTypes.string,
    languageCode: PropTypes.string,
};

export default MainNavLink;
